import { useState } from "react";
import axios from "axios";
import { PageHero } from "@/components/sections/PageHero";
import { COMPANY, IMAGES } from "@/lib/site";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Phone, Mail, MapPin, Clock, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact"
        title={<>Let's <span className="text-[#FF5722]">talk shop.</span></>}
        subtitle="Estimates, program inquiries, or general questions — reach out and we'll get back to you within one business day."
        image={IMAGES.welding}
      />

      <section className="section px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 space-y-8">
          <div>
            <div className="eyebrow mb-3">Direct Line</div>
            <a href={COMPANY.phoneHref} className="font-display text-5xl md:text-6xl tracking-tighter text-white hover:text-[#FF5722] transition-colors block" data-testid="contact-phone">{COMPANY.phone}</a>
          </div>
          <div className="border-t border-white/10 pt-8 space-y-4 text-zinc-200">
            <div className="flex gap-3 items-start"><Mail className="w-5 h-5 text-[#FF5722] shrink-0 mt-0.5" /><a href={COMPANY.emailHref} className="break-all hover:text-[#FF5722]" data-testid="contact-email">{COMPANY.email}</a></div>
            <div className="flex gap-3 items-start"><MapPin className="w-5 h-5 text-[#FF5722] shrink-0 mt-0.5" />{COMPANY.city}</div>
            <div className="flex gap-3 items-start"><Clock className="w-5 h-5 text-[#FF5722] shrink-0 mt-0.5" />{COMPANY.hours}</div>
            <div className="flex gap-3 items-start"><Phone className="w-5 h-5 text-[#FF5722] shrink-0 mt-0.5" />{COMPANY.region}</div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 space-y-6 border border-white/10 p-8 bg-[#0B0B0D]" data-testid="contact-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Name *"><Input value={form.name} onChange={e => upd("name", e.target.value)} required data-testid="contact-name" /></Field>
            <Field label="Email *"><Input type="email" value={form.email} onChange={e => upd("email", e.target.value)} required data-testid="contact-email-input" /></Field>
            <Field label="Phone"><Input value={form.phone} onChange={e => upd("phone", e.target.value)} data-testid="contact-phone-input" /></Field>
            <Field label="Subject"><Input value={form.subject} onChange={e => upd("subject", e.target.value)} data-testid="contact-subject" /></Field>
          </div>
          <Field label="Message *"><Textarea rows={7} value={form.message} onChange={e => upd("message", e.target.value)} required data-testid="contact-message" /></Field>
          <button type="submit" disabled={submitting} className="btn-primary" data-testid="contact-submit">
            {submitting ? "Sending..." : "Send Message"} <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      </section>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest text-zinc-400 mb-2 block">{label}</Label>
    {children}
  </div>
);
